import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { API, getHeaders } from '../portalShared';

export default function BrandProspecting() {
  const [titleKeywords, setTitleKeywords] = useState('');
  const [states, setStates] = useState('');
  const [count, setCount] = useState(25);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { fetchHistory(); }, []);
  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/prospecting/history`, { headers: getHeaders() });
      setHistory(data.searches || []);
    } catch {}
  };

  const handleSearch = async () => {
    if (!titleKeywords.trim()) return;
    setSearching(true);
    try {
      const { data } = await axios.post(`${API}/portal/prospecting/search`, {
        title_keywords: titleKeywords.split(',').map(s => s.trim()).filter(Boolean),
        states: states ? states.split(',').map(s => s.trim()).filter(Boolean) : [],
        count,
      }, { headers: getHeaders() });
      setResults(data);
      fetchHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Search failed'); }
    finally { setSearching(false); }
  };

  return (
    <div className="space-y-6" data-testid="brand-prospecting">
      <h2 className="font-display text-xl font-bold">Executive <span className="text-[#FF4343]">Prospecting</span></h2>
      <p className="text-xs text-[#9CA3AF]">Search for verified executives to invite to your campaigns. $0.25 per contact returned.</p>

      <div className="bg-[#111827] border border-white/10 p-4 sm:p-6 space-y-4">
        <div>
          <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Job Titles (comma separated)</label>
          <input value={titleKeywords} onChange={e => setTitleKeywords(e.target.value)} placeholder="e.g., Dealer Principal, General Manager, Service Director" data-testid="prospect-titles"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Locations (optional)</label>
            <input value={states} onChange={e => setStates(e.target.value)} placeholder="e.g., United States, California" data-testid="prospect-states"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          </div>
          <div>
            <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Max Results</label>
            <select value={count} onChange={e => setCount(Number(e.target.value))} data-testid="prospect-count"
              className="w-full px-3 py-2 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              {[10,25,50,100].map(n => <option key={n} value={n}>{n} contacts (${(n * 0.25).toFixed(2)})</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleSearch} disabled={searching || !titleKeywords.trim()} data-testid="prospect-search-btn"
          className="w-full flex items-center justify-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2.5 text-sm font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
          {searching ? 'Searching...' : <><Search className="w-4 h-4" /> Search Executives</>}
        </button>
      </div>

      {results && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6" data-testid="prospect-results">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">{results.total} Executives Found</h3>
            <span className="text-xs font-mono text-[#FF4343]">Cost: ${results.total_cost}</span>
          </div>
          <div className="space-y-2">
            {results.results?.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-white/5 p-3" data-testid={`prospect-result-${i}`}>
                <div>
                  <span className="text-sm text-white font-medium">{r.name}</span>
                  <span className="text-xs text-[#6B7280] ml-2">{r.title}</span>
                  <p className="text-[10px] text-[#6B7280]">{r.company}{r.city ? ` · ${r.city}` : ''}{r.state ? `, ${r.state}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.has_email && <span className="text-[10px] text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5">Email</span>}
                  {r.has_phone && <span className="text-[10px] text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5">Phone</span>}
                  {r.linkedin_url && <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400">LinkedIn</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Search History</h3>
          <div className="space-y-2">
            {history.slice(0, 10).map(h => (
              <div key={h.id} className="flex items-center justify-between bg-[#111827] border border-white/10 p-3 text-xs">
                <div>
                  <span className="text-white">{h.criteria?.title_keywords?.join(', ')}</span>
                  <span className="text-[#6B7280] ml-2">{h.results_count} results</span>
                </div>
                <div className="text-right">
                  <span className="font-mono text-[#FF4343]">${h.total_cost}</span>
                  <p className="text-[10px] text-[#6B7280]">{new Date(h.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

